import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Award, TrendingDown, ArrowRight, Sparkles, CheckCircle2, Scale, BarChart3, PiggyBank, Target, BookOpen, GraduationCap, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Home = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div className="container mx-auto px-6 py-20 md:py-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 space-y-6">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-5 py-2">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Smart College Financial Planning</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
              Know the True Cost
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
                Before You Commit
              </span>
            </h1>
            
            <p className="text-base lg:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Stop relying on sticker prices. Calculate your real college costs with EFC estimation, regional adjustments, inflation projections, and lifetime ROI analysis.
            </p>
            
            <div className="flex gap-4 justify-center flex-wrap pt-4">
              <Link to="/calculator">
                <Button data-testid="calculate-cost-btn" size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base shadow-lg shadow-blue-600/20 transition-all duration-200 hover:shadow-xl">
                  Calculate Your True Cost
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/compare">
                <Button data-testid="compare-colleges-btn" size="lg" variant="outline" className="border-2 border-slate-700 bg-slate-900 text-white hover:bg-slate-800 hover:border-slate-600 px-8 py-6 text-base">
                  <Scale className="w-5 h-5 mr-2" /> Compare Colleges
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-900 border-y border-slate-800 py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1">200+</p>
              <p className="text-slate-500 text-sm">Colleges</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-emerald-400 mb-1">50</p>
              <p className="text-slate-500 text-sm">States Covered</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-blue-400 mb-1">150+</p>
              <p className="text-slate-500 text-sm">Scholarships</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1">Free</p>
              <p className="text-slate-500 text-sm">Forever</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Powerful Tools for Smart Decisions</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">Everything you need to understand college costs and make informed financial decisions.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <Card className="bg-slate-900 border border-slate-800 p-6 hover:border-blue-500/50 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
              <Calculator className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Advanced Calculator</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              EFC calculation, regional cost adjustments, inflation projections, and detailed cost breakdowns with visual charts.
            </p>
          </Card>

          <Card className="bg-slate-900 border border-slate-800 p-6 hover:border-emerald-500/50 transition-all duration-300">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
              <BarChart3 className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">ROI Analysis</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              See your lifetime earnings premium, calculate return on investment, and understand if your degree is worth the cost.
            </p>
          </Card>

          <Card className="bg-slate-900 border border-slate-800 p-6 hover:border-blue-500/50 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
              <Scale className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">College Comparison</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Compare up to 4 schools side-by-side. See costs, acceptance rates, and find your best value option instantly.
            </p>
          </Card>

          <Card className="bg-slate-900 border border-slate-800 p-6 hover:border-emerald-500/50 transition-all duration-300">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Debt Payoff Simulator</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Interactive slider to see how extra payments reduce interest. Plan your payoff strategy before you even start.
            </p>
          </Card>

          <Card className="bg-slate-900 border border-slate-800 p-6 hover:border-blue-500/50 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
              <PiggyBank className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Savings Goal Calculator</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Know exactly how much to save monthly. We calculate your gap and show you realistic savings targets.
            </p>
          </Card>

          <Card className="bg-slate-900 border border-slate-800 p-6 hover:border-emerald-500/50 transition-all duration-300">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
              <TrendingDown className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Alternative Finder</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Discover schools with similar academics but lower costs. Smart matching by major, ranking, and acceptance rates.
            </p>
          </Card>
        </div>
      </div>

      {/* Why This Matters Section */}
      <div className="bg-slate-900/50 py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center">
              Why True Cost Matters
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4 items-start bg-slate-900 p-6 rounded-xl border border-slate-800">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Sticker price isn't reality</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Most families make college decisions based on published tuition rates without understanding their actual out-of-pocket costs after aid, scholarships, and grants.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4 items-start bg-slate-900 p-6 rounded-xl border border-slate-800">
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">In-state tuition saves thousands</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Public universities offer dramatically lower rates for state residents. Our calculator automatically applies in-state pricing when you select your home state.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4 items-start bg-slate-900 p-6 rounded-xl border border-slate-800">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Your major determines ROI</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    A $200k education might make sense for a Computer Science major but not for Fine Arts. We show you expected earnings using real BLS data and calculate whether your investment will pay off.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-10 md:p-14 text-center shadow-xl shadow-blue-600/20">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Award className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Make a Smart Decision?
            </h2>
            <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
              Start with our calculator to understand your true costs, then explore scholarships and compare your options.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/calculator">
                <Button data-testid="get-started-btn" size="lg" className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-6 text-base font-semibold shadow-lg transition-all duration-200">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/scholarships">
                <Button data-testid="find-scholarships-btn" size="lg" variant="outline" className="border-2 border-white/30 bg-transparent text-white hover:bg-white/10 px-8 py-6 text-base">
                  <Award className="w-5 h-5 mr-2" /> Find Scholarships
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="border-t border-slate-800 py-10">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500">
            <Link to="/about" className="hover:text-blue-400 transition-colors">About</Link>
            <Link to="/faq" className="hover:text-blue-400 transition-colors">FAQ</Link>
            <Link to="/resources" className="hover:text-blue-400 transition-colors">Resources</Link>
            <Link to="/contact" className="hover:text-blue-400 transition-colors">Contact</Link>
            <Link to="/privacy" className="hover:text-blue-400 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-blue-400 transition-colors">Terms</Link>
          </div>
          <p className="text-center text-slate-600 text-sm mt-4">
            Data sources: Bureau of Labor Statistics, NACE, College Board, and official state financial aid websites.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Home;
